import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import DpjpCombobox from "@/components/DpjpCombobox";
import { toast } from "sonner";
import { ChevronDown, Link2, UserRoundPlus, ShieldQuestion } from "lucide-react";
import { SEMUA, filterGroups, groupByDepartemen, groupTitle, validFilter } from "@/lib/dpjpVerifikasiGroups";

// Master Data tab: DPJP names typed with "Lainnya (isi manual)" on /ajukan (dpjp_terverifikasi: false), grouped per
// Departemen and spelling. "Petakan" points those records at an existing Master DPJP, or adds the name to the Master
// first. Older free-text records (from before Master DPJP) are not listed. Shown per Departemen (A-Z), each group
// collapsible, with a Departemen filter; the grouping and order live in lib/dpjpVerifikasiGroups.js.
export default function DpjpBelumTerverifikasi({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [master, setMaster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [mode, setMode] = useState("pilih");              // "pilih" an existing DPJP | "baru" add to Master
  const [pilihan, setPilihan] = useState({ dpjp_id: null, dpjp_nama: "", manual: false });
  const [namaBaru, setNamaBaru] = useState("");
  const [saving, setSaving] = useState(false);
  const [ksmFilter, setKsmFilter] = useState(SEMUA);
  const [collapsed, setCollapsed] = useState(() => new Set());   // group keys folded by the user; all open by default

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, m] = await Promise.all([api.get("/master/dpjp-belum-terverifikasi"), api.get("/master/dpjp")]);
      setRows(u.data);
      setMaster(m.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = (r) => {
    const options = master.filter((d) => d.ksm_id === r.ksm_id);
    setTarget(r);
    setMode(options.length ? "pilih" : "baru");
    setPilihan({ dpjp_id: null, dpjp_nama: "", manual: false });
    setNamaBaru(r.nama);
  };

  const save = async () => {
    const body = { ksm_id: target.ksm_id, kunci: target.kunci };
    if (mode === "pilih") {
      if (!pilihan.dpjp_id) { toast.error("Pilih DPJP dari daftar"); return; }
      body.dpjp_id = pilihan.dpjp_id;
    } else {
      if (!namaBaru.trim()) { toast.error("Isi nama DPJP"); return; }
      body.nama_baru = namaBaru.trim();
    }
    setSaving(true);
    try {
      const { data } = await api.post("/master/dpjp-belum-terverifikasi/petakan", body);
      toast.success(`${data.dipetakan} pasien dipetakan ke ${data.dpjp.nama}`);
      setTarget(null);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const options = target ? master.filter((d) => d.ksm_id === target.ksm_id) : [];

  const groups = useMemo(() => groupByDepartemen(rows), [rows]);
  // After a Petakan empties the filtered Departemen, go back to every Departemen.
  useEffect(() => { setKsmFilter((k) => validFilter(groups, k)); }, [groups]);
  const shown = filterGroups(groups, validFilter(groups, ksmFilter));

  const toggleGroup = (key) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (!next.delete(key)) next.add(key);
    return next;
  });

  const groupToggle = (g, className = "") => (
    <button type="button" onClick={() => toggleGroup(g.key)} aria-expanded={!collapsed.has(g.key)}
      className={`flex w-full items-start gap-2 text-left ${className}`} data-testid={`dpjp-verifikasi-group-${g.key}`}>
      <ChevronDown size={16} className={`mt-0.5 shrink-0 text-slate-500 transition-transform ${collapsed.has(g.key) ? "-rotate-90" : ""}`} />
      <span className="min-w-0 break-words font-semibold text-slate-800">{groupTitle(g)}</span>
    </button>
  );

  const ejaanLain = (r) => r.ejaan.length > 1 && (
    <p className="mt-0.5 text-xs text-slate-500 break-words">
      Ejaan lain: {r.ejaan.filter((e) => e.nama !== r.nama).map((e) => `${e.nama} (${e.jumlah})`).join("; ")}
    </p>
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500 flex items-start gap-2">
        <ShieldQuestion size={14} className="mt-0.5 shrink-0 text-amber-500" />
        <span>
          Nama DPJP yang diisi manual ("Lainnya") di form pengajuan — {rows.length} nama. Petakan ke DPJP di Master,
          atau tambahkan ke Master bila memang DPJP baru. Data pasien lama (sebelum ada Master DPJP) tidak ditampilkan di sini.
        </span>
      </p>
      {groups.length > 0 && (
        <div className="space-y-1.5 sm:w-72">
          <Label className="text-xs font-medium text-slate-500">Departemen</Label>
          <Select value={validFilter(groups, ksmFilter)} onValueChange={setKsmFilter}>
            <SelectTrigger data-testid="dpjp-verifikasi-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value={SEMUA}>Semua departemen</SelectItem>
              {groups.map((g) => <SelectItem key={g.key} value={g.key}>{g.ksm_nama}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {/* Phones: one card per name, the Petakan button within reach without scrolling sideways. */}
      <div className="space-y-3 sm:hidden" data-testid="dpjp-verifikasi-cards">
        {loading ? <p className="py-10 text-center text-sm text-slate-400">Memuat data...</p>
          : rows.length === 0 ? <p className="py-10 text-center text-sm text-slate-400">Tidak ada DPJP yang perlu diverifikasi.</p>
          : shown.map((g) => (
            <section key={g.key} className="space-y-2">
              {groupToggle(g, "min-h-10 rounded-lg bg-slate-100 px-3 py-2.5 text-sm")}
              {!collapsed.has(g.key) && g.rows.map((r) => (
                <Card key={`${r.ksm_id}|${r.kunci}`} className="border-slate-200/80 shadow-sm p-3.5 space-y-2">
                  <div>
                    <p className="font-medium text-slate-800 break-words">{r.nama}</p>
                    <p className="text-xs text-slate-500">{r.jumlah} pasien · {fmtDateTime(r.terakhir)}</p>
                    {ejaanLain(r)}
                  </div>
                  <Button size="sm" variant="outline" className="h-10 w-full gap-1 text-xs" onClick={() => open(r)}>
                    <Link2 size={13} /> Petakan
                  </Button>
                </Card>
              ))}
            </section>
          ))}
      </div>
      <Card className="hidden sm:block border-slate-200/80 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="table-dpjp-verifikasi">
            <thead className="bg-[#267B84] text-white">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold">Nama diketik</th>
                <th className="text-right px-4 py-3 text-xs font-semibold">Pasien</th>
                <th className="text-left px-4 py-3 text-xs font-semibold">Pengajuan terakhir</th>
                <th className="text-left px-4 py-3 text-xs font-semibold">Aksi</th>
              </tr>
            </thead>
            {loading || rows.length === 0 ? (
              <tbody className="bg-white">
                <tr><td colSpan={4} className="px-4 py-14 text-center text-slate-400">
                  {loading ? "Memuat data..." : "Tidak ada DPJP yang perlu diverifikasi."}
                </td></tr>
              </tbody>
            ) : shown.map((g) => (
              <tbody key={g.key} className="divide-y divide-slate-100 bg-white border-t border-slate-200">
                <tr className="bg-slate-50">
                  <td colSpan={4} className="px-4 py-2.5 text-sm">{groupToggle(g)}</td>
                </tr>
                {!collapsed.has(g.key) && g.rows.map((r) => (
                  <tr key={`${r.ksm_id}|${r.kunci}`} className="hover:bg-slate-50 align-top" data-testid={`dpjp-verifikasi-row-${r.kunci}`}>
                    <td className="px-4 py-3 min-w-[220px]">
                      <p className="font-medium text-slate-800">{r.nama}</p>
                      {ejaanLain(r)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{r.jumlah}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                      {fmtDateTime(r.terakhir)}
                      <span className="block font-mono text-[11px] text-slate-400">{r.pengajuan.map((p) => p.kode_pengajuan).filter(Boolean).slice(0, 3).join(", ")}</span>
                    </td>
                    <td className="px-4 py-3">
                      <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => open(r)} data-testid={`petakan-${r.kunci}`}>
                        <Link2 size={13} /> Petakan
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      </Card>

      <Dialog open={Boolean(target)} onOpenChange={(v) => !v && !saving && setTarget(null)}>
        <DialogContent className="max-w-lg bg-white" data-testid="petakan-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Petakan DPJP</DialogTitle>
            <DialogDescription className="text-xs">
              "{target?.nama}" — {target?.ksm_nama}, {target?.jumlah} pasien. Nama DPJP pada pasien-pasien ini akan diganti
              dengan nama di Master (nama yang diketik tetap tersimpan). Tercatat di Audit Log.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex rounded-lg border border-slate-200 p-0.5 bg-slate-50 w-fit">
              {[["pilih", "Pilih dari Master", Link2], ["baru", "Tambah DPJP baru", UserRoundPlus]].map(([k, label, Icon]) => (
                <button key={k} type="button" onClick={() => setMode(k)} disabled={k === "pilih" && options.length === 0}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors disabled:opacity-40 ${
                    mode === k ? "bg-[#00B9AD] text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                  data-testid={`petakan-mode-${k}`}>
                  <Icon size={13} /> {label}
                </button>
              ))}
            </div>
            {mode === "pilih" ? (
              <div className="space-y-1.5">
                <Label htmlFor="petakan-dpjp" className="text-xs font-medium text-slate-600">DPJP di Master ({target?.ksm_nama})</Label>
                <DpjpCombobox id="petakan-dpjp" options={options} value={pilihan} onChange={setPilihan} allowLainnya={false} testId="petakan-dpjp" />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="petakan-nama-baru" className="text-xs font-medium text-slate-600">Nama DPJP baru (ditambahkan ke Master)</Label>
                <Input id="petakan-nama-baru" value={namaBaru} maxLength={100} onChange={(e) => setNamaBaru(e.target.value)} data-testid="petakan-nama-baru" />
                <p className="text-xs text-slate-500">Rapikan dulu ejaan dan gelarnya; nama ini yang muncul di daftar pilihan form pengajuan.</p>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setTarget(null)} disabled={saving}>Batal</Button>
              <Button onClick={save} disabled={saving} className="bg-[#00B9AD] hover:bg-[#009E94] text-white" data-testid="petakan-simpan">
                {saving ? "Menyimpan..." : "Petakan"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
